import { useState, useEffect, useCallback } from 'react';
import { analyticsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Thermometer, Droplets, Wind, Gauge, RefreshCw, Wifi, Radio, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const AQI_CONFIG = {
  good: { label: 'Bra', color: 'bg-emerald-500', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  moderate: { label: 'Måttlig', color: 'bg-amber-400', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
  unhealthy: { label: 'Ohälsosam', color: 'bg-orange-500', bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700' },
  hazardous: { label: 'Farlig', color: 'bg-red-600', bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
};

function QualityBar({ value, max, thresholds, unit, label, icon: Icon }) {
  const pct = Math.min((value / max) * 100, 100);
  let color = 'bg-emerald-500';
  if (value > thresholds[2]) color = 'bg-red-500';
  else if (value > thresholds[1]) color = 'bg-orange-500';
  else if (value > thresholds[0]) color = 'bg-amber-400';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 font-ibm flex items-center gap-1">
          <Icon className="h-3 w-3" />{label}
        </span>
        <span className="text-sm font-ibm font-semibold tabular-nums">{value?.toFixed(1) ?? '—'} {unit}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AirQualityPage() {
  const [data, setData] = useState([]);
  const [tamperAlerts, setTamperAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [aqRes, tamperRes] = await Promise.all([
        analyticsApi.airQuality(),
        analyticsApi.tamperCheck(),
      ]);
      setData(aqRes.data);
      setTamperAlerts(tamperRes.data);
      setLastRefresh(new Date());
    } catch {
      toast.error('Kunde inte ladda luftkvalitetsdata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="air-quality-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Luftkvalitet
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Realtidsövervakning av inomhusmiljön — temperatur, fuktighet, partiklar och VOC
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-slate-400 font-ibm">
              Uppdaterad {lastRefresh.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={loadData} className="font-ibm gap-2" data-testid="refresh-aq">
            <RefreshCw className="h-4 w-4" /> Uppdatera
          </Button>
        </div>
      </div>

      {/* Driftvarningar */}
      {tamperAlerts.length > 0 ? (
        <Card className="border-amber-200 bg-amber-50" data-testid="tamper-alerts">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <span className="font-ibm font-semibold text-amber-800 text-sm">Driftvarningar</span>
            </div>
            <div className="space-y-2">
              {tamperAlerts.map(a => (
                <div key={a.device_id} className="flex items-start justify-between gap-4 py-2 border-b border-amber-100 last:border-0" data-testid={`tamper-alert-${a.device_id}`}>
                  <div className="min-w-0">
                    <p className="font-ibm font-medium text-amber-900 text-sm truncate">{a.device_name}</p>
                    <p className="text-xs text-amber-700 mt-0.5">{a.message}. {a.detail}</p>
                  </div>
                  <span className="text-xs text-amber-600 font-ibm whitespace-nowrap shrink-0">
                    Senaste data: {a.last_seen_relative}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-200 bg-emerald-50" data-testid="tamper-alerts-empty">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <span className="text-emerald-600 font-bold">✓</span>
              <span className="font-ibm text-emerald-800 text-sm">Alla sensorer fungerar normalt</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Room Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.map(room => {
          const aqi = AQI_CONFIG[room.aqi_level] || AQI_CONFIG.good;
          const cur = room.current;
          const avg = room.avg_1h;

          return (
            <Card key={room.room_id} className={cn("border transition-colors", aqi.bg)} data-testid={`aq-room-${room.room_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-chivo text-lg">{room.room_name}</CardTitle>
                    <p className="text-xs text-slate-500 font-ibm mt-0.5">{room.school_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {room.connection_type === 'wifi'
                      ? <Wifi className="h-4 w-4 text-purple-500" />
                      : <Radio className="h-4 w-4 text-sky-500" />}
                    <Badge className={cn("font-ibm text-xs text-white", aqi.color)}>
                      {aqi.label}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Big numbers row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <Thermometer className="h-5 w-5 mx-auto text-orange-500 mb-1" />
                    <p className="text-2xl font-chivo font-black text-slate-900 tabular-nums">
                      {cur.temperature?.toFixed(1) ?? '—'}°
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-ibm">Temperatur</p>
                  </div>
                  <div className="text-center p-3 bg-white/60 rounded-lg">
                    <Droplets className="h-5 w-5 mx-auto text-blue-500 mb-1" />
                    <p className="text-2xl font-chivo font-black text-slate-900 tabular-nums">
                      {cur.humidity?.toFixed(0) ?? '—'}%
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-ibm">Fuktighet</p>
                  </div>
                </div>

                {/* Quality bars */}
                <div className="space-y-3">
                  <QualityBar value={cur.pm2_5} max={100} thresholds={[12, 35, 55]} unit="µg/m³" label="PM2.5" icon={Gauge} />
                  <QualityBar value={cur.voc} max={600} thresholds={[100, 200, 400]} unit="ppb" label="VOC" icon={Wind} />
                  <QualityBar value={cur.pm10} max={150} thresholds={[50, 100, 150]} unit="µg/m³" label="PM10" icon={Gauge} />
                </div>

                {/* 1h avg footer */}
                <div className="pt-2 border-t border-slate-200/60 flex justify-between text-[10px] text-slate-400 font-ibm uppercase tracking-widest">
                  <span>1h snitt: {avg.pm2_5} PM2.5 · {avg.voc} VOC</span>
                  <span>{avg.readings} avl.</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {data.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Wind className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-slate-700">Ingen sensordata tillgänglig</h3>
            <p className="text-slate-500 mt-1 font-ibm">Lägg till sensorer för att börja övervaka luftkvaliteten</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
